/* ── WORK_TRACK — Board (tickets) ─────────────────────────── */

import {
  $,
  $$,
  esc,
  icon,
  api,
  apiUpload,
  state,
  toast,
  registerView,
  go,
  openModal,
  closeModal,
  setAfterLoadHook,
  projectOptions,
  memberOptions,
  activeMembers,
  memberName,
  avatarHtml,
  typeBadge,
  priorityBadge,
  statusBadge,
  assigneeChip,
  resolvedByChip,
  setFieldError,
  clearFieldErrors,
  fmtDate,
  TYPE_LABEL,
  STATUS_LABEL
} from './core';
import { loadDashboard } from './dashboard';
import { highlightMentions, attachMentionAutocomplete, setCommentMentionHandler } from './mentions';
import type { Member, Project, Task, TaskAttachment, TaskComment, TaskHistoryEntry, TaskStatus } from './types';

setCommentMentionHandler(taskId => {
  go('board');
  openTicketModal(taskId);
});

// Deep link: open a ticket straight from ?ticket=<id> in the URL.
setAfterLoadHook(() => {
  const id = new URLSearchParams(window.location.search).get('ticket');
  if (id && /^\d+$/.test(id)) {
    go('board');
    openTicketModal(Number(id));
  }
});

const HISTORY_FIELDS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  type: 'Type',
  status: 'Status',
  priority: 'Priority',
  order_index: 'Order',
  due_date: 'Due date',
  project_id: 'Project',
  assignee_id: 'Assignee',
  resolved_by_id: 'Resolved by',
  created: 'Created'
};

function boardFilterParams(): URLSearchParams {
  const p = new URLSearchParams();
  const q = $<HTMLInputElement>('#boardSearch').value.trim();
  if (q) p.set('q', q);
  const project = $<HTMLSelectElement>('#boardProjectFilter').value;
  if (project) p.set('project_id', project);
  const assignee = $<HTMLSelectElement>('#boardAssigneeFilter').value;
  if (assignee) p.set('assignee', assignee);
  const reporter = $<HTMLSelectElement>('#boardReporterFilter').value;
  if (reporter) p.set('reporter', reporter);
  const priority = $<HTMLSelectElement>('#boardPriorityFilter').value;
  if (priority) p.set('priority', priority);
  const type = $<HTMLSelectElement>('#boardTypeFilter').value;
  if (type) p.set('type', type);
  const status = $<HTMLSelectElement>('#boardStatusFilter').value;
  if (status) p.set('status', status);
  return p;
}

function boardHasFilters(): boolean {
  return [...boardFilterParams().entries()].length > 0;
}

async function refreshBoard(): Promise<void> {
  try {
    const params = boardFilterParams();
    const qs = params.toString();
    state.boardTasks = await api<Task[]>(qs ? `/api/tasks?${qs}` : '/api/tasks');
    renderBoard();
    $('#boardClearFilters').classList.toggle('hidden', !boardHasFilters());
  } catch (e) {
    toast((e as Error).message, true);
  }
}

export async function loadBoard(): Promise<void> {
  try {
    const [projects, members] = await Promise.all([
      state.projects.length ? Promise.resolve(state.projects) : api<Project[]>('/api/projects'),
      state.members.length ? Promise.resolve(state.members) : api<Member[]>('/api/users').catch(() => [])
    ]);
    state.projects = projects;
    state.members = members;

    const sel = $<HTMLSelectElement>('#boardProjectFilter');
    const current = sel.value;
    sel.innerHTML =
      '<option value="">All projects</option>' +
      state.projects.map(p => `<option value="${p.id}">${esc(p.name)}</option>`).join('');
    sel.value = current || '';

    const asel = $<HTMLSelectElement>('#boardAssigneeFilter');
    const acurrent = asel.value;
    asel.innerHTML =
      '<option value="">All assignees</option>' +
      activeMembers()
        .map(m => `<option value="${m.id}">${esc(memberName(m))}</option>`)
        .join('');
    asel.value = acurrent || '';

    const rsel = $<HTMLSelectElement>('#boardReporterFilter');
    const rcurrent = rsel.value;
    rsel.innerHTML =
      '<option value="">All reporters</option>' +
      activeMembers()
        .map(m => `<option value="${m.id}">${esc(memberName(m))}</option>`)
        .join('');
    rsel.value = rcurrent || '';

    await refreshBoard();
  } catch (e) {
    toast((e as Error).message, true);
  }
}

const STATUS_OPTIONS: TaskStatus[] = ['todo', 'approved', 'in_progress', 'done'];
function statusOptions(current?: TaskStatus): string {
  return STATUS_OPTIONS.map(
    s => `<option value="${s}" ${current === s ? 'selected' : ''}>${STATUS_LABEL[s]}</option>`
  ).join('');
}

function ticketRowHtml(t: Task): string {
  return `
    <li class="ticket-row" data-task="${t.id}">
      <div class="tr-main">
        <div class="tr-title">
          <span class="tr-id">#${t.id}</span>
          <span class="tr-name" title="Open ticket">${esc(t.title)}</span>
        </div>
        <div class="tr-badges">
          ${typeBadge(t.type)} ${priorityBadge(t.priority)} ${statusBadge(t.status)}
          ${t.project_name ? `<span class="badge badge-project">${icon('package', 12)} ${esc(t.project_name)}</span>` : ''}
        </div>
      </div>
      <div class="tr-meta">
        ${t.resolved_by_id ? resolvedByChip(t) : assigneeChip(t)}
        ${t.due_date ? `<span class="tr-due">${icon('calendar', 13)} Due ${esc(t.due_date)}</span>` : ''}
      </div>
      <div class="tr-status">
        <select class="status-select" data-status="${t.id}" title="Change progress">${statusOptions(t.status)}</select>
      </div>
    </li>`;
}

function renderBoard(): void {
  const ul = $('#ticketList');
  if (!ul) return;
  const items = state.boardTasks.slice().sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
  if (!items.length) {
    const hasFilter = boardHasFilters();
    ul.innerHTML = `<li class="empty ticket-empty">${hasFilter ? 'No tickets match your filters.' : 'No tickets yet — create your first one.'}</li>`;
    return;
  }
  ul.innerHTML = items.map(ticketRowHtml).join('');
}

document.addEventListener('click', e => {
  const target = e.target as HTMLElement;
  const trow = target.closest('.ticket-row');
  if (trow && !target.closest('select, a, input, label, button')) {
    openTicketModal(Number(trow.getAttribute('data-task')));
  }
});

document.addEventListener('change', e => {
  const target = e.target as HTMLElement;
  const sel = target.closest('[data-status]') as HTMLSelectElement | null;
  if (sel) {
    const id = Number(sel.dataset.status);
    const status = sel.value as TaskStatus;
    if (state.tasks.find(x => x.id === id)?.status === status) return;
    moveTask(id, status);
  }
});

$<HTMLSelectElement>('#boardProjectFilter').addEventListener('change', refreshBoard);
$<HTMLSelectElement>('#boardAssigneeFilter').addEventListener('change', refreshBoard);
$<HTMLSelectElement>('#boardReporterFilter').addEventListener('change', refreshBoard);
$<HTMLSelectElement>('#boardPriorityFilter').addEventListener('change', refreshBoard);
$<HTMLSelectElement>('#boardTypeFilter').addEventListener('change', refreshBoard);
$<HTMLSelectElement>('#boardStatusFilter').addEventListener('change', refreshBoard);
$('#boardClearFilters').addEventListener('click', () => {
  [
    'boardSearch',
    'boardProjectFilter',
    'boardAssigneeFilter',
    'boardReporterFilter',
    'boardPriorityFilter',
    'boardTypeFilter',
    'boardStatusFilter'
  ].forEach(id => {
    $<HTMLInputElement>(`#${id}`).value = '';
  });
  refreshBoard();
});
let boardSearchTimer: number | null = null;
$<HTMLInputElement>('#boardSearch').addEventListener('input', () => {
  if (boardSearchTimer) clearTimeout(boardSearchTimer);
  boardSearchTimer = window.setTimeout(refreshBoard, 300);
});

async function moveTask(id: number, status: TaskStatus): Promise<void> {
  try {
    const d = await api<Task>(`/api/tasks/${id}`, 'PUT', { status });
    const t = state.tasks.find(x => x.id === id);
    if (t) Object.assign(t, d);
    state.boardTasks = state.boardTasks.map(x => (x.id === id ? d : x));
    renderBoard();
    if (state.view === 'dashboard') loadDashboard();
  } catch (e) {
    toast((e as Error).message, true);
  }
}

async function deleteTask(id: number): Promise<void> {
  if (!confirm('Delete this ticket? Its comments, history and time entries will also be removed.')) return;
  try {
    await api(`/api/tasks/${id}`, 'DELETE');
    state.tasks = state.tasks.filter(t => t.id !== id);
    state.boardTasks = state.boardTasks.filter(t => t.id !== id);
    renderBoard();
    toast('Ticket deleted');
  } catch (e) {
    toast((e as Error).message, true);
  }
}

// ── Ticket modal (combined view + edit) ──────────────────
function personChipHtml(label: string, userId: number | null, name: string | null, avatar: string | null): string {
  if (!userId)
    return `<div class="person"><span class="person-label">${label}</span><span class="person muted">—</span></div>`;
  const initials = (name || '?').slice(0, 2).toUpperCase();
  return `<div class="person"><span class="person-label">${label}</span><span class="assignee-chip">${avatarHtml(initials, userId, avatar || null)} <span>${esc(name)}</span></span></div>`;
}

export async function openTicketModal(id: number, startInEdit = false): Promise<void> {
  const pool = state.view === 'board' ? state.boardTasks : state.tasks;
  const t =
    pool.find(x => x.id === id) || state.tasks.find(x => x.id === id) || state.boardTasks.find(x => x.id === id);
  if (!t) {
    toast('Ticket not found', true);
    return;
  }
  const card = openModal(
    `
    <form id="ticketForm" class="modal-form">
      <div class="modal-head">
        <h2>Ticket details</h2>
        <div class="modal-head-actions">
          <button type="button" class="btn btn-ghost btn-sm" id="ticketShare" title="Copy ticket link">${icon('share', 14)} Share</button>
          <button type="button" class="modal-close">${icon('x', 16)}</button>
        </div>
      </div>
      <div class="field"><label>Title</label><input name="title" required maxlength="200" value="${esc(t.title)}"><em class="field-err" role="alert"></em></div>
      <div class="field"><label>Description</label><textarea name="description" maxlength="5000">${esc(t.description || '')}</textarea></div>
      <div class="row2">
        <div class="field"><label>Project</label>
          <select name="project_id">${t.project_id ? projectOptions(Number(t.project_id)) : `<option value="">No project</option>` + projectOptions()}</select>
        </div>
        <div class="field"><label>Type</label>
          <select name="type">
            ${(['task', 'bug', 'feature', 'implementation'] as const).map(tp => `<option value="${tp}" ${t.type === tp ? 'selected' : ''}>${TYPE_LABEL[tp]}</option>`).join('')}
          </select>
        </div>
      </div>
      <div class="row2">
        <div class="field"><label>Priority</label>
          <select name="priority">
            ${(['low', 'medium', 'high', 'urgent'] as const).map(p => `<option value="${p}" ${t.priority === p ? 'selected' : ''}>${p}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>Assignee</label>
          <select name="assignee_id">
            <option value="">Unassigned</option>
            ${memberOptions(t.assignee_id ?? undefined)}
          </select>
        </div>
      </div>
      <div class="row2">
        <div class="field"><label>Due date</label><input type="date" name="due_date" value="${esc(t.due_date || '')}"></div>
        <div class="field"><label>Progress</label>
          <select name="status">${statusOptions(t.status)}</select>
        </div>
      </div>
      <div class="detail-people">
        ${personChipHtml('Created by', t.reporter_id, t.reporter_name, t.reporter_avatar)}
        ${personChipHtml('Assigned to', t.assignee_id, t.assignee_name, t.assignee_avatar)}
        ${personChipHtml('Resolved by', t.resolved_by_id, t.resolved_by_name, t.resolved_by_avatar)}
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-ghost" id="modalCancel">Cancel</button>
        <button type="button" class="btn btn-danger btn-sm" id="ticketDelete">Delete</button>
        <button type="submit" class="btn btn-primary">Save</button>
      </div>
    </form>
    <div class="detail-attachments">
      <h3>Attachments</h3>
      <div class="attach-add-row">
        <button type="button" class="btn btn-ghost btn-sm" id="detailAttachBtn">${icon('paperclip', 14)} Add attachment</button>
        <input type="file" id="detailAttachInput" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.md" hidden>
      </div>
      <ul id="attachmentsList" class="attach-list detail-attach-list"></ul>
    </div>
    <div class="detail-cols">
      <div class="detail-col">
        <h3>Comments</h3>
        <form id="commentForm" class="comment-form">
          <textarea name="body" rows="2" placeholder="Add a comment…" required></textarea>
          <button type="submit" class="btn btn-primary btn-sm">Post</button>
        </form>
        <ul id="commentsList" class="comment-list"></ul>
      </div>
      <div class="detail-col">
        <button type="button" class="collapse-toggle" id="historyToggle" aria-expanded="false">
          <span class="collapse-caret">${icon('chevronDown', 14)}</span> History
        </button>
        <ul id="historyList" class="history-list collapse-body" hidden></ul>
      </div>
    </div>
  `,
    true
  );

  const actionsEl = card.querySelector<HTMLElement>('.modal-actions')!;
  let editing = startInEdit;

  card.querySelector<HTMLButtonElement>('#ticketShare')?.addEventListener('click', () => {
    const link = `${location.origin}${location.pathname}?ticket=${t.id}`;
    navigator.clipboard.writeText(link).then(
      () => toast('Ticket link copied to clipboard'),
      () => toast('Copy failed', true)
    );
  });

  const setFormDisabled = (disabled: boolean): void => {
    card
      .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        '#ticketForm input, #ticketForm select, #ticketForm textarea'
      )
      .forEach(el => {
        el.disabled = disabled;
      });
  };
  setFormDisabled(!editing);

  const descField = card.querySelector<HTMLTextAreaElement>('#ticketForm textarea[name="description"]');
  if (descField) {
    const resizeDesc = (): void => {
      descField.style.height = 'auto';
      descField.style.height = `${Math.min(descField.scrollHeight, window.innerHeight * 0.45)}px`;
    };
    resizeDesc();
    descField.addEventListener('input', resizeDesc);
  }

  const renderActions = (): void => {
    actionsEl.innerHTML = `
      <button type="button" class="btn btn-ghost" id="modalCancel">Close</button>
      <button type="button" class="btn btn-danger btn-sm" id="ticketDelete">Delete</button>
      ${
        editing
          ? '<button type="submit" class="btn btn-primary">Save</button>'
          : `<button type="button" class="btn btn-primary" id="ticketEdit">${icon('edit', 14)} Edit</button>`
      }
    `;
    card.querySelector<HTMLButtonElement>('#modalCancel')!.addEventListener('click', closeModal);
    card.querySelector<HTMLButtonElement>('#ticketDelete')!.addEventListener('click', () => {
      closeModal();
      deleteTask(id);
    });
    const editBtn2 = card.querySelector<HTMLButtonElement>('#ticketEdit');
    if (editBtn2) {
      editBtn2.addEventListener('click', () => {
        editing = true;
        setFormDisabled(false);
        renderActions();
        const titleField = card.querySelector<HTMLInputElement>('#ticketForm input[name="title"]');
        if (titleField) titleField.focus();
      });
    }
  };
  renderActions();

  const historyToggle = card.querySelector<HTMLButtonElement>('#historyToggle');
  if (historyToggle) {
    historyToggle.addEventListener('click', () => {
      const body = card.querySelector<HTMLElement>('#historyList');
      const expanded = historyToggle.getAttribute('aria-expanded') === 'true';
      historyToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (body) body.hidden = expanded;
    });
  }

  const daBtn = card.querySelector<HTMLButtonElement>('#detailAttachBtn');
  const daInput = card.querySelector<HTMLInputElement>('#detailAttachInput');
  if (daBtn && daInput) {
    daBtn.addEventListener('click', () => daInput.click());
    daInput.addEventListener('change', async () => {
      const file = daInput.files && daInput.files[0];
      daInput.value = '';
      if (!file) return;
      if ((file.size || 0) > 20 * 1024 * 1024) {
        toast('File is over 20 MB', true);
        return;
      }
      try {
        await apiUpload(`/api/tasks/${id}/attachments`, file);
        loadAttachments(id);
        toast('Attachment added');
      } catch (err) {
        toast((err as Error).message, true);
      }
    });
  }

  loadComments(id);
  loadHistory(id);
  loadAttachments(id);

  const commentInput = card.querySelector<HTMLTextAreaElement>('#commentForm textarea[name="body"]');
  if (commentInput) attachMentionAutocomplete(commentInput, () => state.members);

  card.querySelector<HTMLFormElement>('#commentForm')!.addEventListener('submit', async e => {
    e.preventDefault();
    const text = card.querySelector<HTMLTextAreaElement>('textarea[name="body"]')!.value.trim();
    if (!text) return;
    try {
      await api(`/api/tasks/${id}/comments`, 'POST', { body: text });
      card.querySelector<HTMLFormElement>('#commentForm')!.reset();
      loadComments(id);
      toast('Comment posted');
    } catch (err) {
      toast((err as Error).message, true);
    }
  });

  let submitting = false;
  const ticketFormEl = card.querySelector<HTMLFormElement>('#ticketForm')!;
  ticketFormEl.addEventListener('submit', async e => {
    e.preventDefault();
    if (submitting) return;
    clearFieldErrors(card);
    const f = new FormData(ticketFormEl);
    const title = f.get('title');
    if (!title || !String(title).trim()) {
      const inp = card.querySelector<HTMLInputElement>('input[name="title"]')!;
      setFieldError(inp, 'Title is required');
      inp.focus();
      return;
    }
    submitting = true;
    const sb = card.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (sb) {
      sb.disabled = true;
      sb.classList.add('loading');
      sb.innerHTML = `<span class="spinner"></span>Saving…`;
    }
    const payload = {
      title: String(title).trim(),
      description: String(f.get('description') || ''),
      project_id: f.get('project_id') ? Number(f.get('project_id')) : null,
      type: f.get('type') as string,
      priority: f.get('priority') as string,
      status: f.get('status') as string,
      assignee_id: f.get('assignee_id') ? Number(f.get('assignee_id')) : null,
      due_date: f.get('due_date') ? String(f.get('due_date')) : null
    };
    try {
      const d = await api<Task>(`/api/tasks/${id}`, 'PUT', payload);
      const idx = state.tasks.findIndex(x => x.id === id);
      if (idx >= 0) state.tasks[idx] = d;
      state.boardTasks = state.boardTasks.map(x => (x.id === id ? d : x));
      if (state.view === 'board') refreshBoard();
      else renderBoard();
      if (state.view === 'dashboard') loadDashboard();
      closeModal();
      toast('Ticket saved');
    } catch (err) {
      toast((err as Error).message, true);
    } finally {
      submitting = false;
      const b2 = card.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (b2) {
        b2.disabled = false;
        b2.classList.remove('loading');
        b2.innerHTML = 'Save';
      }
    }
  });
}

async function loadComments(id: number): Promise<void> {
  const ul = $('#commentsList');
  if (!ul) return;
  ul.innerHTML = '<li class="empty">Loading…</li>';
  try {
    const comments = await api<TaskComment[]>(`/api/tasks/${id}/comments`);
    if (!comments.length) {
      ul.innerHTML = '<li class="empty">No comments yet.</li>';
      return;
    }
    ul.innerHTML = comments
      .map(
        c => `
      <li class="comment-item">
        <div class="comment-head"><strong>${esc(c.author_name || 'Unknown')}</strong><span class="muted">${fmtDate(c.created_at)}</span></div>
        <div class="comment-body">${highlightMentions(esc(c.body))}</div>
        ${state.user && (['owner', 'admin'].includes(state.user.role) || c.user_id === state.user.id) ? `<button class="comment-del" data-cdel="${c.id}" title="Delete comment">${icon('x', 14)}</button>` : ''}
      </li>`
      )
      .join('');
    $$<HTMLButtonElement>('#commentsList [data-cdel]').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (!confirm('Delete this comment?')) return;
        try {
          await api(`/api/comments/${btn.dataset.cdel}`, 'DELETE');
          loadComments(id);
          toast('Comment deleted');
        } catch (err) {
          toast((err as Error).message, true);
        }
      });
    });
  } catch {
    ul.innerHTML = '<li class="empty">Could not load.</li>';
  }
}

// Show readable names for ID-based fields in history instead of raw numbers.
function historyValue(field: string, raw: string | null): string {
  if (!raw) return '';
  if (field === 'assignee_id' || field === 'resolved_by_id') {
    const m = (state.members || []).find(x => x.id === Number(raw));
    return m ? m.name || m.username || raw : raw;
  }
  if (field === 'project_id') {
    const p = (state.projects || []).find(x => x.id === Number(raw));
    if (p) return p.name;
  }
  return raw;
}

async function loadHistory(id: number): Promise<void> {
  const ul = $('#historyList');
  if (!ul) return;
  ul.innerHTML = '<li class="empty">Loading…</li>';
  try {
    const history = await api<TaskHistoryEntry[]>(`/api/tasks/${id}/history`);
    if (!history.length) {
      ul.innerHTML = '<li class="empty">No activity yet.</li>';
      return;
    }
    ul.innerHTML = history
      .map(h => {
        const actor = esc(h.actor_name || 'Unknown');
        const when = fmtDate(h.created_at);
        let line: string;
        if (h.field === 'created') {
          line = `created this ticket`;
        } else {
          const field = esc(HISTORY_FIELDS[h.field] || h.field);
          line = `changed ${field}`;
          if (h.old_value || h.new_value) {
            const oldV = historyValue(h.field, h.old_value);
            const newV = historyValue(h.field, h.new_value);
            line += `: <em>${esc(oldV || '—')}</em> → <em>${esc(newV || '—')}</em>`;
          }
        }
        return `<li class="history-item"><div><strong>${actor}</strong> ${line}</div><div class="muted">${when}</div></li>`;
      })
      .join('');
  } catch {
    ul.innerHTML = '<li class="empty">Could not load.</li>';
  }
}

// ── New ticket modal ─────────────────────────────────────
$('#newTaskBtn').addEventListener('click', () => openTaskModal());

function openTaskModal(): void {
  if (!state.projects.length) {
    toast('Create a project first', true);
    go('projects');
    return;
  }
  const pendingFiles: File[] = [];
  const card = openModal(
    `
    <form class="modal-form" id="taskForm">
      <div class="modal-head"><h2>New ticket</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
      <div class="ai-draft-panel">
        <div class="ai-draft-head">${icon('zap', 15)} Draft with AI</div>
        <div class="ai-draft-row">
          <input id="aiHint" placeholder="Describe the idea — e.g. ‘A user can filter tickets by assignee’" maxlength="600" autocomplete="off">
          <button type="button" class="btn btn-sm btn-primary" id="aiDraftBtn">Draft</button>
        </div>
        <div class="ai-draft-note" id="aiDraftNote">Sketch an idea and let AI write the title + description for you.</div>
      </div>
      <div class="field"><label>Title</label><input name="title" required maxlength="200" value=""><em class="field-err" role="alert"></em></div>
      <div class="field"><label>Description</label><textarea name="description" maxlength="5000"></textarea></div>
      <div class="row2">
        <div class="field"><label>Project</label>
          <select name="project_id"><option value="">No project</option>${projectOptions()}</select>
        </div>
        <div class="field"><label>Type</label>
          <select name="type">
            ${(['task', 'bug', 'feature', 'implementation'] as const).map(tp => `<option value="${tp}">${TYPE_LABEL[tp]}</option>`).join('')}
          </select>
        </div>
      </div>
      <div class="row2">
        <div class="field"><label>Priority</label>
          <select name="priority">
            ${(['low', 'medium', 'high', 'urgent'] as const).map(p => `<option value="${p}">${p}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>Assignee</label>
          <select name="assignee_id">
            <option value="">Unassigned</option>
            ${memberOptions()}
          </select>
        </div>
      </div>
      <div class="row2">
        <div class="field"><label>Due date</label><input type="date" name="due_date" value=""></div>
        <div class="field">
          <label>Attachments${pendingFiles.length ? ` <span class="opt">(${pendingFiles.length})</span>` : ''}</label>
          <button type="button" class="btn btn-ghost btn-sm attach-add" id="attachAddBtn">${icon('paperclip', 14)} Add file or screenshot</button>
          <input type="file" id="attachInput" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.md" multiple hidden>
          <ul class="attach-list" id="attachList"></ul>
          <em class="field-err" role="alert"></em>
        </div>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-ghost" id="modalCancel">Cancel</button>
        <button type="submit" class="btn btn-primary">Create</button>
      </div>
    </form>`
  );
  card.querySelector<HTMLButtonElement>('#modalCancel')!.addEventListener('click', closeModal);

  // AI draft
  const aiBtn = card.querySelector<HTMLButtonElement>('#aiDraftBtn');
  if (aiBtn) {
    const aiHint = card.querySelector<HTMLInputElement>('#aiHint')!;
    const aiNote = card.querySelector<HTMLElement>('#aiDraftNote');
    aiBtn.addEventListener('click', async () => {
      const hint = aiHint.value.trim();
      if (!hint) {
        toast('Describe your idea above first', true);
        aiHint.focus();
        return;
      }
      const projectId = card.querySelector<HTMLSelectElement>('select[name="project_id"]')!.value;
      const proj = state.projects.find(p => String(p.id) === String(projectId));
      aiBtn.disabled = true;
      aiBtn.classList.add('loading');
      aiBtn.innerHTML = `<span class="spinner"></span>Drafting…`;
      if (aiNote) aiNote.textContent = 'AI is drafting the title and description…';
      try {
        const draft = await api<{ title: string; description: string }>('/api/ai/draft', 'POST', {
          hint,
          type: card.querySelector<HTMLSelectElement>('select[name="type"]')!.value,
          project: proj ? proj.name : ''
        });
        card.querySelector<HTMLInputElement>('input[name="title"]')!.value = draft.title || hint.slice(0, 200);
        card.querySelector<HTMLTextAreaElement>('textarea[name="description"]')!.value = draft.description || '';
        if (aiNote) {
          aiNote.textContent = 'Draft ready — review and tweak before creating.';
        }
      } catch {
        if (aiNote) aiNote.textContent = 'Could not reach AI — creating a quick local draft.';
        const proj2 = proj ? `; related project: ${proj.name}` : '';
        card.querySelector<HTMLInputElement>('input[name="title"]')!.value = hint.slice(0, 200);
        card.querySelector<HTMLTextAreaElement>('textarea[name="description"]')!.value =
          `Work on: ${hint}${proj2}. Clarify requirements and expected outcome, then confirm acceptance criteria before marking done.`;
      } finally {
        aiBtn.disabled = false;
        aiBtn.classList.remove('loading');
        aiBtn.innerHTML = `Draft`;
      }
    });
  }

  // Attachments (create mode)
  const attachAddBtn = card.querySelector<HTMLButtonElement>('#attachAddBtn');
  const attachInput = card.querySelector<HTMLInputElement>('#attachInput');
  const attachList = card.querySelector<HTMLElement>('#attachList');
  if (attachAddBtn && attachInput) {
    const renderFiles = (): void => {
      if (!attachList) return;
      if (!pendingFiles.length) {
        attachList.innerHTML = '';
        return;
      }
      attachList.innerHTML = pendingFiles
        .map(
          (f, i) => `
        <li class="attach-chip">
          ${
            f.type && f.type.startsWith('image/')
              ? `<span class="attach-thumb">${URL.createObjectURL(f)}</span>`
              : `<span class="attach-ico">${icon('paperclip', 15)}</span>`
          }
          <span class="attach-name" title="${esc(f.name)}">${esc(f.name)}</span>
          <span class="attach-meta">${(f.size / 1024).toFixed(0)} KB</span>
          <button type="button" class="attach-del" data-idx="${i}" aria-label="Remove">${icon('x', 14)}</button>
        </li>`
        )
        .join('');
      attachList.querySelectorAll<HTMLButtonElement>('.attach-del').forEach(bn => {
        bn.addEventListener('click', () => {
          pendingFiles.splice(Number(bn.dataset.idx), 1);
          renderFiles();
        });
      });
    };
    attachAddBtn.addEventListener('click', () => attachInput.click());
    attachInput.addEventListener('change', () => {
      const incoming = Array.from(attachInput.files || []);
      attachInput.value = '';
      incoming.forEach(f => {
        if ((f.size || 0) > 20 * 1024 * 1024) {
          toast(`${f.name} is over 20 MB`, true);
          return;
        }
        pendingFiles.push(f);
      });
      renderFiles();
    });
  }

  let submitting = false;
  const taskFormEl = card.querySelector<HTMLFormElement>('#taskForm')!;
  taskFormEl.addEventListener('submit', async e => {
    e.preventDefault();
    if (submitting) return;
    clearFieldErrors(card);
    const f = new FormData(taskFormEl);
    const title = f.get('title');
    const projectId = f.get('project_id');
    if (!title || !String(title).trim()) {
      const inp = card.querySelector<HTMLInputElement>('input[name="title"]')!;
      setFieldError(inp, 'Title is required');
      inp.focus();
      return;
    }
    submitting = true;
    const submitBtn = card.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
      submitBtn.innerHTML = `<span class="spinner"></span>Creating…`;
    }
    const payload = {
      title: String(title).trim(),
      description: String(f.get('description') || ''),
      project_id: projectId ? Number(projectId) : null,
      type: f.get('type') as string,
      priority: f.get('priority') as string,
      assignee_id: f.get('assignee_id') ? Number(f.get('assignee_id')) : null,
      due_date: f.get('due_date') ? String(f.get('due_date')) : null
    };
    try {
      const d = await api<Task>('/api/tasks', 'POST', payload);
      state.tasks.unshift(d);
      if (state.view === 'board') refreshBoard();
      else renderBoard();
      // upload any pending attachments to the freshly created ticket
      if (pendingFiles.length) {
        for (const file of pendingFiles) {
          try {
            await apiUpload(`/api/tasks/${d.id}/attachments`, file);
          } catch (err) {
            toast(`Could not upload ${file.name}: ${(err as Error).message}`, true);
          }
        }
        pendingFiles.length = 0;
      }
      if (attachList) attachList.innerHTML = '';
      closeModal();
      toast('Ticket created');
    } catch (err) {
      toast((err as Error).message, true);
    } finally {
      submitting = false;
      const sb = card.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (sb) {
        sb.disabled = false;
        sb.classList.remove('loading');
        sb.innerHTML = 'Create';
      }
    }
  });
}

async function loadAttachments(taskId: number): Promise<void> {
  const ul = $('#attachmentsList');
  if (!ul) return;
  ul.innerHTML = '<li class="empty">Loading…</li>';
  try {
    const atts = await api<TaskAttachment[]>(`/api/tasks/${taskId}/attachments`);
    if (!atts.length) {
      ul.innerHTML = '<li class="empty">No attachments.</li>';
      return;
    }
    ul.innerHTML = atts
      .map(
        a => `
      <li class="attach-chip">
        ${
          a.mime && a.mime.startsWith('image/')
            ? `<span class="attach-thumb"><img src="${esc(a.url)}" alt=""></span>`
            : `<span class="attach-ico">${icon('paperclip', 15)}</span>`
        }
        <a class="attach-name" href="${esc(a.url)}" target="_blank" rel="noopener" title="${esc(a.filename)}">${esc(a.filename)}</a>
        <span class="attach-meta">${((a.size || 0) / 1024).toFixed(0)} KB</span>
        <button type="button" class="attach-del" data-aid="${a.id}" aria-label="Delete">${icon('trash', 14)}</button>
      </li>`
      )
      .join('');
    ul.querySelectorAll<HTMLButtonElement>('.attach-del').forEach(bn => {
      bn.addEventListener('click', async () => {
        if (!confirm('Delete this attachment?')) return;
        try {
          await api(`/api/attachments/${bn.dataset.aid}`, 'DELETE');
          loadAttachments(taskId);
          toast('Attachment deleted');
        } catch (err) {
          toast((err as Error).message, true);
        }
      });
    });
  } catch {
    ul.innerHTML = '<li class="empty">Could not load attachments.</li>';
  }
}

registerView('board', loadBoard);
